import { FC, useEffect, useState, useMemo } from 'react'
import { GetServerSideProps } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

import Auth from 'store/auth'
import RecommendationContext from 'context/recommendationContext'
import SEOContext from 'context/SEOContext'
import MainLayout from 'containers/main-layout/main-layout'
import WebinarEnded from 'components/articles/article-video/article-video'
import ArticleVideoPlaylist from 'components/articles/article-video-playlist/article-video-playlist'
import SEOMetatags from 'components/parts/seo-metatags/seo-metatags'
import SEOBreadCrumbs from 'components/parts/seo-metatags/seo-breadcrumbs'
import WebinarWithSocket from 'components/webinar/webinar-with-socket/webinar-with-socket'
const LikeWrap = dynamic(() => import('containers/like-wrap/like-wrap'))
const AdminEditBtn = dynamic(() => import('components/parts/admin-edit-btn/admin-edit-btn'))
import useRecommendation from 'hooks/useRecomendation'
import useNMO from 'store/analytics-nmo/useNMO'
import useUnratedWebinar from 'store/common/useUnratedWebinar'
import useSetView from 'hooks/useSetView'
import useLayoutAbsolute from 'store/common/useLayoutAbsolute'
import useFuturePageVisited from 'hooks/useFuturePageVisited'

import nestRequest from 'utils/server-side/nestRequest'
import { getDigitFromString, linkWithTranslation } from 'utils/getURLData'
import { go404 } from 'utils/redirect'
import { formatWebinarSEO } from 'utils/metrics/formatSEO'
import { getAxiosSingle } from 'pages/api/axios'
import { WebinarBC } from 'constants/SEO-breadcrumbs'
import { Pathname, StatusVocabulary } from 'constants/data'
import { IMaterial } from 'constants/types/webinar.types'
import { IContext } from 'constants/types/next.types'
import { ISEOContext } from 'constants/types/common.types'
import { BASE_URL } from 'constants/url'
import { IMaterialCard } from 'constants/types/card.types'

interface WebinarPage {
  webinar: IMaterial
  ABVersion: string
}

const WebinarPage: FC<WebinarPage> = ({ webinar, ABVersion }) => {
  const webinarID = webinar?.id
  const { asPath, query } = useRouter()
  const initSEO = useMemo(() => formatWebinarSEO(webinar, asPath), [webinarID])

  const [SEOData, setSEOData] = useState<ISEOContext>(initSEO)
  const [status, setStatus] = useState(webinar?.StatusId) //вспомогательный стейт из-за того, что есть состояние "после вебинара"
  const [playlist, setPlaylist] = useState<IMaterialCard[]>([])

  const router = useRouter()
  const { dopId, origin } = query
  const { setNMOPoings } = useNMO()
  const { layoutStatus } = useLayoutAbsolute()
  const { isAdmin } = Auth.useAuth()
  const recommendData = useRecommendation(webinarID, 'webinar', 2)
  const { removeUnratedWebinar } = useUnratedWebinar()
  const isVideoproject = Boolean(webinar.playlistId?.length)

  const SEOWebinarBC = useMemo(() => {
    const { TITLE, LINK } = formatWebinarSEO(webinar, asPath)
    return WebinarBC({ id: LINK, name: TITLE })
  }, [webinarID])

  useEffect(() => {
    if (isVideoproject && webinar.playlistId?.length) {
      const isFromVideoproject = Boolean(dopId) && origin === 'videoproject'

      const playlistId = isFromVideoproject
        ? webinar.playlistId.find((element) => element === Number(dopId))
        : webinar.playlistId[webinar.playlistId.length - 1]

      const getWebinars = async () => {
        const webinarsData = await getAxiosSingle(
          `${BASE_URL.WEBINAR}?playlistId=${playlistId}&limit=50`
        )
        const articlesData = await getAxiosSingle(
          `${BASE_URL.ARTICLE}?playlistId=${playlistId}&limit=50`
        )

        const webinarsResult = webinarsData.status === 404 ? [] : webinarsData.rows
        const articlesResult = articlesData.status === 404 ? [] : articlesData.rows
        const currPlaylist = await getAxiosSingle(`${BASE_URL.VIDEOPROJECTS}/${playlistId}`)

        const result = [...webinarsResult, ...articlesResult]
        const orderedResult = result
          .map((material) => {
            const { id: materialId, materialType } = material
            const playlistElem = currPlaylist.list.find(
              (video: any) => video.materialId === materialId && video.materialType === materialType
            )
            return { ...material, order: playlistElem?.index }
          })
          .sort((a, b) => a.order - b.order)

        setPlaylist(orderedResult)
      }
      getWebinars()
    }
  }, [])

  useEffect(() => {
    setStatus(webinar?.StatusId)

    if (webinar.linkTranslation && !router.asPath.includes(webinar.linkTranslation)) {
      const preparedLink = linkWithTranslation(router.asPath, webinar.linkTranslation)
      router.replace(preparedLink, undefined, { shallow: true })
    }
  }, [webinar])

  useEffect(() => {
    setNMOPoings(webinar.points || 0)

    return () => {
      setNMOPoings(0)
    }
  }, [webinar.points])

  useEffect(() => {
    removeUnratedWebinar()
    setSEOData(formatWebinarSEO(webinar, asPath))

    if (webinar.forceRedirectURL && window.location.href !== webinar.forceRedirectURL) {
      router.push(webinar.forceRedirectURL)
    }
  }, [webinarID])

  useFuturePageVisited({ id: webinar?.id, status })
  useSetView({
    materialId: webinarID,
    materialType: 'webinar',
    isForceCancel: webinar?.StatusId === StatusVocabulary.FUTURE,
  })

  const isPast = status === StatusVocabulary.PAST

  const renderWebinar = () => {
    switch (true) {
      case isVideoproject && isPast:
        return <ArticleVideoPlaylist dataset={webinar} isWebinar playlistData={playlist} />
      case isPast:
        return <WebinarEnded dataset={webinar} isWebinar />
      default:
        return <WebinarWithSocket webinarData={{ ...webinar, ABVersion }} setSEOData={setSEOData} />
    }
  }

  return (
    <>
      <SEOMetatags data={SEOData} />
      <SEOBreadCrumbs data={SEOWebinarBC} />

      <SEOContext.Provider value={SEOData}>
        <MainLayout noFooter={!isPast} isAbsolute={layoutStatus} noBtnUp={!isPast} noRaitingTable>
          <RecommendationContext.Provider value={recommendData}>
            <LikeWrap>{renderWebinar()}</LikeWrap>
          </RecommendationContext.Provider>
        </MainLayout>
      </SEOContext.Provider>

      {isAdmin && (
        <AdminEditBtn path={`createWebinar/${getDigitFromString(router.query.id as string)}`} />
      )}
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  let webinar = {}

  const ABVersion = process.env.AB_VERSION

  const goNoRecord = () => ({
    redirect: {
      destination: `/${Pathname.WEBINAR}/norec`,
      permanent: false,
    },
  })

  const expiredRec = () => ({
    redirect: {
      destination: `/${Pathname.WEBINAR}/expiredrec`,
      permanent: false,
    },
  })

  const comeLater = () => ({
    redirect: {
      destination: `/${Pathname.WEBINAR}/defer`,
      permanent: false,
    },
  })

  const forceRedirect = () => ({
    redirect: {
      destination: `/${Pathname.WEBINAR}/1060`,
      permanent: false,
    },
  })

  if (context?.params?.id) {
    try {
      const { userData } = (context as IContext).req
      const { isAdmin } = userData || {}

      const webinarId = getDigitFromString(context.params.id as string)
      if (Number(webinarId) === 1059) return forceRedirect()

      if (!webinarId) return { notFound: true }

      const restRet = await nestRequest(context, 'get', `/api/webinar/${webinarId}`, {})
      const recievedData = restRet?.data

      if (restRet.status === 404) return go404() // не нашли материал
      if (!Object.keys(recievedData)?.length) return go404() // пришел пустой объект

      const isPast = recievedData.StatusId === StatusVocabulary.PAST
      const isPrivateAccessDenied =
        !isAdmin && recievedData.isPrivate && context?.query?.key !== recievedData.privateKey
      const isDeferredRecord = isPast && Boolean(recievedData.deferredRecordPublication) === true
      const isWithoutRecord = isPast && Boolean(recievedData.withoutRecord) === true
      const isExpirated = !isAdmin && new Date(recievedData.expirationDate).getTime() <= Date.now()

      if (recievedData.isPaid === true) {
        let paidData: any = {}
        try {
          paidData = await nestRequest(
            context,
            'get',
            `/api/payment/check?materialType=webinar&materialId=${webinarId}`,
            {}
          )
        } catch (err) {
          console.log(err)
        }

        const successResult = isAdmin ? true : Boolean(paidData?.data?.result === 'success')
        if (isPast || successResult) recievedData.isAcceptAllow = successResult
        else return go404()
        webinar = recievedData
      } else if (!isPrivateAccessDenied) webinar = recievedData
      if (isDeferredRecord) return comeLater()
      if (isWithoutRecord) return goNoRecord()
      if (isPrivateAccessDenied) return go404()
      if (isExpirated) return expiredRec()

      if (
        recievedData.linkTranslation &&
        recievedData.linkTranslation != (context.params.id as string)
      ) {
        const key = context?.query?.key
        return {
          redirect: {
            destination: `/${Pathname.WEBINAR}/${recievedData.linkTranslation}${
              key ? `?key=${key}` : ''
            }`,
            statusCode: 307,
          },
        }
      }
    } catch (err) {
      console.warn(err)
      return go404()
    }
  }

  return {
    props: {
      webinar,
      ABVersion: ABVersion || 'A',
    },
  }
}

export default WebinarPage
